'use strict';

document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const g76Toast = (message, duration = 4200) => {
  const toast = document.querySelector('[data-toast]');
  if (!toast) return;
  toast.textContent = message;
  toast.classList.add('is-visible');
  window.clearTimeout(toast._timer);
  toast._timer = window.setTimeout(() => toast.classList.remove('is-visible'), duration);
};

const g76EscapeText = (value) => String(value || '').trim();

class G76Header {
  constructor(header) {
    this.header = header;
    this.button = header.querySelector('[data-mobile-menu-toggle]');
    this.menu = header.querySelector('[data-mobile-menu]');
    this.menuIcon = header.querySelector('[data-menu-icon]');
    this.closeIcon = header.querySelector('[data-close-icon]');

    if (!this.button || !this.menu) return;
    this.button.addEventListener('click', () => this.toggle());
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 990) this.close();
    });
  }

  toggle() {
    const isOpen = this.button.getAttribute('aria-expanded') === 'true';
    isOpen ? this.close() : this.open();
  }

  open() {
    this.button.setAttribute('aria-expanded', 'true');
    this.menu.hidden = false;
    this.menuIcon.hidden = true;
    this.closeIcon.hidden = false;
    document.body.classList.add('g76-menu-open');
  }

  close() {
    this.button.setAttribute('aria-expanded', 'false');
    this.menu.hidden = true;
    this.menuIcon.hidden = false;
    this.closeIcon.hidden = true;
    document.body.classList.remove('g76-menu-open');
  }
}

document.querySelectorAll('[data-site-header]').forEach((header) => new G76Header(header));

class StickerProduct extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.form = this.querySelector('form');
    if (this.form) this.form.addEventListener('submit', this.onSubmit);
    this.bindUploadDropzone();
    this.syncCustomFields();
  }

  onChange(event) {
    if (event.target.matches('[data-option-value-id]')) {
      this.updateVariant(event.target);
      return;
    }

    if (event.target.matches('[data-artwork-input]')) {
      this.validateArtwork(event.target);
    }
  }

  onClick(event) {
    const thumbnail = event.target.closest('[data-media-src]');
    if (thumbnail && this.contains(thumbnail)) {
      this.selectThumbnail(thumbnail);
      return;
    }

    const quoteButton = event.target.closest('[data-quote-button]');
    if (quoteButton && this.contains(quoteButton)) {
      this.requestQuote();
    }
  }

  onSubmit() {
    const button = this.querySelector('[data-add-to-cart]');
    if (!button || button.disabled) return;
    button.classList.add('is-loading');
    const label = button.querySelector('span');
    if (label) label.textContent = 'ADDING TO CART...';
  }

  async updateVariant(changedInput) {
    const optionPicker = this.querySelector('[data-sticker-fragment="options"]');
    if (!optionPicker) return;

    const selectedInputs = Array.from(optionPicker.querySelectorAll('[data-option-value-id]:checked'));
    const selectedIds = selectedInputs.map((input) => input.dataset.optionValueId).filter(Boolean);
    const productUrl = changedInput.dataset.productUrl || this.dataset.productUrl;
    const sectionId = this.dataset.sectionId;

    if (!productUrl || !sectionId || selectedIds.length === 0) return;

    const fetchUrl = new URL(productUrl, window.location.origin);
    fetchUrl.searchParams.set('section_id', sectionId);
    fetchUrl.searchParams.set('option_values', selectedIds.join(','));

    this.classList.add('is-updating');
    this.setOptionsDisabled(true);

    try {
      const response = await fetch(fetchUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
      const responseText = await response.text();
      const parsed = new DOMParser().parseFromString(responseText, 'text/html');

      ['media', 'options', 'summary', 'submit'].forEach((fragmentName) => {
        const currentFragment = this.querySelector(`[data-sticker-fragment="${fragmentName}"]`);
        const newFragment = parsed.querySelector(`[data-sticker-fragment="${fragmentName}"]`);
        if (currentFragment && newFragment) currentFragment.replaceWith(newFragment);
      });

      const publicUrl = new URL(productUrl, window.location.origin);
      publicUrl.searchParams.set('option_values', selectedIds.join(','));
      window.history.replaceState({}, '', `${publicUrl.pathname}${publicUrl.search}`);
      this.dataset.productUrl = publicUrl.pathname;
      this.syncCustomFields();
      this.announceVariant();
    } catch (error) {
      console.error(error);
      g76Toast('We could not update that option. Please try again.');
    } finally {
      this.classList.remove('is-updating');
      this.setOptionsDisabled(false);
    }
  }

  setOptionsDisabled(disabled) {
    this.querySelectorAll('[data-option-value-id]').forEach((input) => {
      if (input.closest('.is-unavailable')) return;
      input.disabled = disabled;
    });
  }

  syncCustomFields() {
    const fields = Array.from(this.querySelectorAll('[data-option-position]'));
    let customSize = false;
    let customQuantity = false;

    fields.forEach((fieldset) => {
      const selected = fieldset.querySelector('[data-option-value-id]:checked');
      if (!selected) return;
      const optionName = (fieldset.dataset.optionName || '').toLowerCase();
      const value = (selected.value || '').toLowerCase();
      if (!value.includes('custom')) return;
      if (optionName.includes('size') || optionName.includes('dimension')) customSize = true;
      if (optionName.includes('quantity') || optionName.includes('qty')) customQuantity = true;
    });

    const customWrapper = this.querySelector('[data-custom-fields]');
    const customSizeFields = this.querySelector('[data-custom-size-fields]');
    const customQuantityField = this.querySelector('[data-custom-quantity-field]');
    const widthInput = this.querySelector('[data-custom-width]');
    const heightInput = this.querySelector('[data-custom-height]');
    const quantityInput = this.querySelector('[data-custom-quantity]');

    if (customWrapper) customWrapper.hidden = !(customSize || customQuantity);
    if (customSizeFields) customSizeFields.hidden = !customSize;
    if (customQuantityField) customQuantityField.hidden = !customQuantity;

    this.toggleCustomInput(widthInput, customSize);
    this.toggleCustomInput(heightInput, customSize);
    this.toggleCustomInput(quantityInput, customQuantity);

    const quoteOnly = this.dataset.quoteOnly === 'true';
    const quoteMode = quoteOnly && (customSize || customQuantity);
    const addButton = this.querySelector('[data-add-to-cart]');
    const quoteButton = this.querySelector('[data-quote-button]');

    if (addButton) addButton.hidden = quoteMode;
    if (quoteButton) quoteButton.hidden = !quoteMode;
  }

  toggleCustomInput(input, active) {
    if (!input) return;
    input.disabled = !active;
    input.required = active;
  }

  validateArtwork(input) {
    const file = input.files && input.files[0];
    const filename = this.querySelector('[data-upload-filename]');
    const zone = this.querySelector('[data-upload-zone]');
    if (!file) {
      if (filename) filename.textContent = '';
      if (zone) zone.classList.remove('has-file');
      return;
    }

    const maxMegabytes = Number(this.dataset.maxFileSize || 20);
    const maxBytes = maxMegabytes * 1024 * 1024;
    const allowed = ['png', 'jpg', 'jpeg', 'pdf', 'svg', 'ai', 'eps'];
    const extension = file.name.split('.').pop().toLowerCase();

    if (!allowed.includes(extension)) {
      input.value = '';
      g76Toast('Please upload PNG, JPG, PDF, SVG, AI or EPS artwork.');
      return;
    }

    if (file.size > maxBytes) {
      input.value = '';
      g76Toast(`Artwork must be ${maxMegabytes}MB or smaller.`);
      return;
    }

    if (filename) filename.textContent = file.name;
    if (zone) zone.classList.add('has-file');
  }

  bindUploadDropzone() {
    const zone = this.querySelector('[data-upload-zone]');
    const input = this.querySelector('[data-artwork-input]');
    if (!zone || !input) return;

    ['dragenter', 'dragover'].forEach((eventName) => {
      zone.addEventListener(eventName, (event) => {
        event.preventDefault();
        zone.classList.add('is-dragging');
      });
    });

    ['dragleave', 'drop'].forEach((eventName) => {
      zone.addEventListener(eventName, (event) => {
        event.preventDefault();
        zone.classList.remove('is-dragging');
      });
    });

    zone.addEventListener('drop', (event) => {
      const files = event.dataTransfer && event.dataTransfer.files;
      if (!files || !files.length) return;
      try {
        input.files = files;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (error) {
        console.warn('Browser did not allow assigning dropped files.', error);
      }
    });
  }

  selectThumbnail(thumbnail) {
    if (!thumbnail.dataset.mediaSrc) return;
    const media = this.querySelector('[data-sticker-fragment="media"]');
    if (!media) return;
    const preview = media.querySelector('.cl-po--preview-wrapper');
    const thumbnails = Array.from(this.querySelectorAll('.g76-product-thumbnail'));
    let mainImage = media.querySelector('[data-main-product-image], .g76-product-main-media__image');
    const showArtworkPreview = preview && thumbnail === thumbnails[0];

    if (preview) preview.hidden = !showArtworkPreview;
    if (showArtworkPreview) {
      if (mainImage) mainImage.hidden = true;
    } else {
      if (!mainImage) {
        mainImage = document.createElement('img');
        mainImage.className = 'g76-product-main-media__image';
        mainImage.setAttribute('data-main-product-image', '');
        media.appendChild(mainImage);
      }
      mainImage.hidden = false;
      mainImage.style.objectFit = 'contain';
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('width');
      mainImage.removeAttribute('height');
      mainImage.src = thumbnail.dataset.mediaSrc;
      mainImage.alt = thumbnail.dataset.mediaAlt || this.dataset.productTitle || '';
    }
    thumbnails.forEach((button) => {
      const active = button === thumbnail;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  requestQuote() {
    const requiredInputs = Array.from(this.querySelectorAll('[data-custom-fields] input[required]:not(:disabled)'));
    const invalid = requiredInputs.find((input) => !input.checkValidity());
    if (invalid) {
      invalid.reportValidity();
      return;
    }

    const number = g76EscapeText(this.dataset.whatsappNumber).replace(/\D/g, '');
    if (!number) {
      g76Toast('Add the WhatsApp number in Theme settings before requesting quotes.');
      return;
    }

    const optionLines = Array.from(this.querySelectorAll('[data-option-position]')).map((fieldset) => {
      const selected = fieldset.querySelector('[data-option-value-id]:checked');
      return selected ? `${fieldset.dataset.optionName}: ${selected.value}` : null;
    }).filter(Boolean);

    const width = this.querySelector('[data-custom-width]:not(:disabled)');
    const height = this.querySelector('[data-custom-height]:not(:disabled)');
    const quantity = this.querySelector('[data-custom-quantity]:not(:disabled)');
    const artwork = this.querySelector('[data-artwork-input]');
    const notes = this.querySelector('[name="properties[Special instructions]"]');

    const lines = [
      'Hi 67Stickers, I would like a custom sticker quote.',
      `Product: ${this.dataset.productTitle || 'Custom stickers'}`,
      ...optionLines,
    ];

    if (width && height) lines.push(`Custom dimensions: ${width.value} in x ${height.value} in`);
    if (quantity) lines.push(`Custom quantity: ${quantity.value}`);
    if (artwork && artwork.files && artwork.files[0]) lines.push(`Artwork file ready: ${artwork.files[0].name}`);
    if (notes && notes.value.trim()) lines.push(`Notes: ${notes.value.trim()}`);

    window.open(`https://wa.me/${number}?text=${encodeURIComponent(lines.join('\n'))}`, '_blank', 'noopener');
  }

  announceVariant() {
    const price = this.querySelector('[data-product-price]');
    if (price) g76Toast(`Selection updated. Total ${price.textContent.trim()}.`, 2400);
  }
}

if (!customElements.get('sticker-product')) {
  customElements.define('sticker-product', StickerProduct);
}

// Mobile navigation keyboard support; desktop behavior is unchanged.
(() => {
  const isMobile = () => window.matchMedia('(max-width: 989px)').matches;
  document.querySelectorAll('[data-site-header]').forEach((header) => {
    const toggle = header.querySelector('[data-mobile-menu-toggle]');
    const menu = header.querySelector('[data-mobile-menu]');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', (event) => {
      if (!isMobile()) return;
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      if (open && event.detail === 0) menu.querySelector('a[href]')?.focus();
    });
    document.addEventListener('keydown', (event) => {
      if (!isMobile() || menu.hidden || toggle.getAttribute('aria-expanded') !== 'true') return;
      if (event.key === 'Escape') {
        event.preventDefault();
        toggle.click();
        toggle.focus();
      } else if (event.key === 'Tab') {
        const items = [toggle, ...menu.querySelectorAll('a[href], button:not([disabled]), summary')].filter((el) => el.getClientRects().length);
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) {
          event.preventDefault(); first?.focus();
        }
      }
    });
  });
})();

// Keep artwork timing explicit without replacing Upload-Lift or its file limits.
(() => {
  const track = name => { if (typeof window.clarity === 'function') window.clarity('event', name); };
  document.querySelectorAll('.g67-fast-order').forEach(root => {
    const choice = root.querySelector('.g67-artwork-choice');
    if (!choice) return;
    const radios = Array.from(choice.querySelectorAll('[data-artwork-timing]'));
    const note = choice.querySelector('[data-artwork-later-note]');
    const sync = () => {
      const uploads = Array.from(root.querySelectorAll('.cl-po--option')).filter(option => option.querySelector('input.filepond--browser'));
      if (!uploads.length) return;
      choice.hidden = false;
      const later = radios.some(input => input.checked && input.dataset.artworkTiming === 'later');
      uploads.forEach(option => { option.classList.add('g67-upload-field'); option.hidden = later; });
      note.hidden = !later;
      note.textContent = uploads.some(option => option.querySelector('.filepond--item'))
        ? 'Any artwork already uploaded stays attached. You can also update it from your order confirmation after purchase.'
        : 'Open your confirmation email on your computer to upload after purchase.';
    };
    choice.addEventListener('change', event => {
      if (!event.target.matches('[data-artwork-timing]')) return;
      sync();
      track(event.target.dataset.artworkTiming === 'later' ? 'Artwork later selected' : 'Artwork now selected');
    });
    const observer = new MutationObserver(sync);
    observer.observe(root.querySelector('.g76-option-picker'), {childList:true,subtree:true});
    sync();
    root.addEventListener('upload:added', event => {
      if (!event.detail?.url || event.detail?.meta?.variant === 'thumbnail') return;
      radios.forEach(input => { input.checked = input.dataset.artworkTiming === 'now'; });
      sync();
    });
    root.querySelector('[data-order-shortcut]')?.addEventListener('click', event => {
      event.preventDefault();
      const panel = root.querySelector('.g76-configurator-card');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      panel.scrollIntoView({block:'start',behavior:reducedMotion?'instant':'smooth'});
      panel.querySelector('.g67-order-heading')?.focus({preventScroll:true});
      track('Order options shortcut clicked');
    });
  });
  // Upload-Lift sends a second event for thumbnails; count the original only.
  const uploadedFiles = new Set();
  window.addEventListener('upload:added', event => {
    const file = event.detail;
    if (!file?.url || file.meta?.variant === 'thumbnail' || uploadedFiles.has(file.url)) return;
    uploadedFiles.add(file.url);
    track(window.location.pathname === '/pages/order-upload' ? 'Artwork uploaded after purchase' : 'Artwork upload completed');
  });
})();


/* Monthly deal countdown: first changeover Oct 30, 2026, at 00:00 America/Chicago.
   Thereafter: the 30th of each month (last day of February). Offers are updated manually. */
(() => {
  const product = document.querySelector('sticker-product[data-monthly-deal="true"]');
  const placement = product
    ? (product.querySelector('.g67-offer-reassurance') || product.querySelector('.g76-product-intro__description'))
    : document.querySelector('.g76-deals-v2__shipping');
  if (!placement || document.querySelector('[data-monthly-deal-clock]')) return;
  const dealTimeZone = 'America/Chicago';
  const dealDateParts = new Intl.DateTimeFormat('en-US', { timeZone: dealTimeZone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' });
  function chicagoParts(time){return Object.fromEntries(dealDateParts.formatToParts(new Date(time)).filter(p=>p.type!=='literal').map(p=>[p.type,Number(p.value)]));}
  function chicagoMidnight(year,month,day){const nominal=Date.UTC(year,month-1,day);let utc=nominal+6*3600000;for(let i=0;i<2;i++){const p=chicagoParts(utc);utc+=nominal-Date.UTC(p.year,p.month-1,p.day,p.hour,p.minute,p.second);}return utc;}
  function nextMonthlyDealDeadline(now){const first=chicagoMidnight(2026,10,30);if(now<first)return first;const local=chicagoParts(now);let year=local.year;let month=local.month;let day=Math.min(30,new Date(Date.UTC(year,month,0)).getUTCDate());let target=chicagoMidnight(year,month,day);if(target<=now){month++;if(month===13){month=1;year++;}day=Math.min(30,new Date(Date.UTC(year,month,0)).getUTCDate());target=chicagoMidnight(year,month,day);}return target;}
  const dateLabel = new Intl.DateTimeFormat('en-US', { timeZone: dealTimeZone, month: 'short', day: 'numeric' });
  const fullDateLabel = new Intl.DateTimeFormat('en-US', { timeZone: dealTimeZone, month: 'long', day: 'numeric', year: 'numeric' });
  const clock = document.createElement('aside');
  clock.className = 'g67-deal-countdown';
  clock.dataset.monthlyDealClock = '';
  clock.setAttribute('aria-label', 'Monthly deal countdown');
  clock.hidden = true;
  clock.innerHTML = '<div class="g67-deal-countdown__row"><strong class="g67-deal-countdown__label">Deals change in</strong><span class="g67-deal-countdown__timer" role="timer" aria-live="off"><span class="g67-deal-countdown__piece" aria-hidden="true"><b data-clock-days>00</b><small>d</small></span><span class="g67-deal-countdown__piece" aria-hidden="true"><b data-clock-hours>00</b><small>h</small></span><span class="g67-deal-countdown__piece" aria-hidden="true"><b data-clock-minutes>00</b><small>m</small></span><span class="g67-deal-countdown__piece" aria-hidden="true"><b data-clock-seconds>00</b><small>s</small></span></span></div><div class="g67-deal-countdown__date"><time></time></div>';
  const timer = clock.querySelector('[role="timer"]');
  const date = clock.querySelector('time');
  const fields = ['days', 'hours', 'minutes', 'seconds'].map(unit => clock.querySelector('[data-clock-' + unit + ']'));
  let deadline = 0;
  function updateCountdown() {
    const now = Date.now();
    if (now >= deadline) {
      deadline = nextMonthlyDealDeadline(now);
      date.dateTime = new Date(deadline).toISOString();
      date.textContent = 'New deals ' + dateLabel.format(new Date(deadline)) + ' · 12:00 AM CT';
      date.setAttribute('aria-label', 'New deals on ' + fullDateLabel.format(new Date(deadline)) + ' at midnight Central time');
    }
    const total = Math.max(0, Math.ceil((deadline - now) / 1000));
    const values = [Math.floor(total / 86400), Math.floor(total / 3600) % 24, Math.floor(total / 60) % 60, total % 60];
    fields.forEach((field, index) => { field.textContent = String(values[index]).padStart(2, '0'); });
    timer.setAttribute('aria-label', values[0] + ' days, ' + values[1] + ' hours, ' + values[2] + ' minutes, ' + values[3] + ' seconds remaining');
    clock.hidden = false;
  }
  updateCountdown();
  placement.insertAdjacentElement('afterend', clock);
  window.setInterval(updateCountdown, 1000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) updateCountdown(); });
})();
